import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Loader2,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  LayoutTemplate,
  Move,
  Pin,
  PanelsTopLeft,
  Tag,
  FileText,
  FileStack,
  Wrench,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useProposalWizard,
  type EditableProposalSeed,
} from "../../hooks/useProposalWizard";
import type {
  BuilderMode,
  FixedPagePolicy,
  ProposalTemplate,
  ClientType,
} from "../../types";
import { WizardStep1TemplateSelect } from "./WizardStep1TemplateSelect";
import { WizardStep2ClientInfo } from "./WizardStep2ClientInfo";
import { WizardStep3ProjectContext } from "./WizardStep3ProjectContext";
import { WizardStep4Review } from "./WizardStep4Review";

const STEPS = [
  { id: "template", label: "Template" },
  { id: "client-info", label: "Client" },
  { id: "project", label: "Project" },
  { id: "review", label: "Review" },
] as const;

const CLIENT_TYPE_CONFIG: Record<
  ClientType,
  { label: string; color: string }
> = {
  construction: {
    label: "Construction",
    color: "text-amber-300 bg-amber-500/10 border-amber-500/20",
  },
  restaurant: {
    label: "Restaurant",
    color: "text-blue-300 bg-blue-500/10 border-blue-500/20",
  },
  commercial: {
    label: "Commercial",
    color: "text-purple-300 bg-purple-500/10 border-purple-500/20",
  },
  residential: {
    label: "Residential",
    color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
  },
  other: {
    label: "Other",
    color: "text-white/45 bg-white/5 border-white/10",
  },
};

const BUILDER_MODE_CONFIG: Record<
  BuilderMode,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  prebuilt: {
    label: "Pre-built",
    icon: <LayoutTemplate className="h-4 w-4" />,
    color: "text-cyan-300 bg-cyan-500/10 border-cyan-500/20",
    description:
      "This proposal will start from a simpler reusable template blueprint.",
  },
  drag_drop: {
    label: "Drag & drop",
    icon: <Move className="h-4 w-4" />,
    color: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/20",
    description:
      "This proposal is starting from a template flagged for the richer builder direction.",
  },
};

const FIXED_PAGE_POLICY_CONFIG: Record<
  FixedPagePolicy,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  template_defined: {
    label: "Template pages",
    icon: <PanelsTopLeft className="h-4 w-4" />,
    color: "text-white/45 bg-white/5 border-white/10",
    description:
      "This template mostly controls its own included page mix.",
  },
  brand_defined: {
    label: "Brand-defined fixed pages",
    icon: <Pin className="h-4 w-4" />,
    color: "text-amber-300 bg-amber-500/10 border-amber-500/20",
    description:
      "This template leans on fixed pages defined in Brand Setup.",
  },
  mixed: {
    label: "Mixed fixed pages",
    icon: <Pin className="h-4 w-4" />,
    color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
    description:
      "This template mixes template-specific pages with brand-defined fixed pages.",
  },
};

interface ProposalWizardProps {
  mode?: "create" | "edit";
  initialProposal?: EditableProposalSeed | null;
  initialTemplate?: ProposalTemplate | null;
  onComplete?: (proposalId: string) => void;
}

function getTemplateBuilderMode(
  template: ProposalTemplate | null | undefined
): BuilderMode {
  return template?.builder_mode === "drag_drop" ? "drag_drop" : "prebuilt";
}

function getTemplateFixedPagePolicy(
  template: ProposalTemplate | null | undefined
): FixedPagePolicy {
  if (
    template?.fixed_page_policy === "brand_defined" ||
    template?.fixed_page_policy === "mixed"
  ) {
    return template.fixed_page_policy;
  }

  return "template_defined";
}

function getTemplateClientType(
  template: ProposalTemplate | null | undefined
): ClientType {
  const raw =
    template?.client_type ??
    (template?.category as ClientType | null | undefined) ??
    "other";

  if (
    raw === "construction" ||
    raw === "restaurant" ||
    raw === "commercial" ||
    raw === "residential" ||
    raw === "other"
  ) {
    return raw;
  }

  return "other";
}

function getTemplateServiceCount(
  template: ProposalTemplate | null | undefined
): number {
  if (!template) return 0;

  if (Array.isArray(template.template_services) && template.template_services.length > 0) {
    return template.template_services.length;
  }

  return template.default_line_items?.length ?? 0;
}

function getTemplatePageCount(
  template: ProposalTemplate | null | undefined
): number {
  if (!template) return 0;

  if (
    Array.isArray(template.template_static_pages) &&
    template.template_static_pages.length > 0
  ) {
    return template.template_static_pages.length;
  }

  return template.default_static_page_ids?.length ?? 0;
}

function getTemplateTaxRateLabel(
  template: ProposalTemplate | null | undefined
): string {
  if (!template) return "0.0%";

  const raw =
    typeof template.tax_rate === "number"
      ? template.tax_rate
      : typeof template.default_tax_rate === "number"
        ? template.default_tax_rate
        : 0;

  return `${(raw * 100).toFixed(1)}%`;
}

export function ProposalWizard({
  mode = "create",
  initialProposal = null,
  initialTemplate = null,
  onComplete,
}: ProposalWizardProps) {
  const navigate = useNavigate();
  const wizard = useProposalWizard({
    mode,
    initialProposal,
    initialTemplate,
    onComplete,
  });

  const {
    currentStep,
    answers,
    proposalId,
    isCreating,
    isGenerating,
    error,
    agentMessage,
    selectTemplate,
    submitClientInfo,
    submitProjectContext,
    updateLineItem,
    updateAnswers,
    finalizeProposal,
    goBack,
  } = wizard;

  useEffect(() => {
    if (currentStep === "done" && proposalId) {
      if (onComplete) {
        onComplete(proposalId);
        return;
      }

      navigate({
        to: "/proposal-builder/$id",
        params: { id: proposalId },
      });
    }
  }, [currentStep, proposalId, navigate, onComplete]);

  const currentStepIndex = STEPS.findIndex((s) => s.id === currentStep);
  const isEditMode = mode === "edit";

  const selectedTemplate = answers.template ?? initialTemplate ?? null;

  const selectedBuilderMode = getTemplateBuilderMode(selectedTemplate);
  const selectedBuilderCfg = BUILDER_MODE_CONFIG[selectedBuilderMode];

  const selectedFixedPagePolicy = getTemplateFixedPagePolicy(selectedTemplate);
  const selectedFixedCfg = FIXED_PAGE_POLICY_CONFIG[selectedFixedPagePolicy];

  const selectedClientType = getTemplateClientType(selectedTemplate);
  const selectedClientCfg = CLIENT_TYPE_CONFIG[selectedClientType];

  const selectedServiceCount = getTemplateServiceCount(selectedTemplate);
  const selectedPageCount = getTemplatePageCount(selectedTemplate);
  const selectedTaxRate = getTemplateTaxRateLabel(selectedTemplate);

  return (
    <div className="flex min-h-screen flex-col bg-[#0f0f0f] text-white">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-white/10 bg-[#0f0f0f]/95 px-6 py-3 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-amber-500/20">
            <Sparkles className="h-3.5 w-3.5 text-amber-400" />
          </div>
          <span className="text-sm font-semibold text-white/70">
            {isEditMode ? "Edit proposal" : "New proposal"}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {STEPS.map((step, idx) => {
            const isCompleted = idx < currentStepIndex;
            const isCurrent = step.id === currentStep;

            return (
              <div
                key={step.id}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
                  isCompleted &&
                    "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
                  isCurrent &&
                    "border-amber-500/30 bg-amber-500/10 text-amber-300",
                  !isCompleted &&
                    !isCurrent &&
                    "border-white/10 bg-white/[0.03] text-white/35"
                )}
              >
                {step.label}
              </div>
            );
          })}
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-6xl flex-1 gap-10 px-6 py-10">
        <div className="w-full max-w-3xl">
          <div className="mb-8 flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-amber-500/10">
              <Sparkles className="h-4 w-4 text-amber-400" />
            </div>

            <div>
              <p className="text-xs font-medium uppercase tracking-[0.14em] text-amber-400/80">
                AI guide
              </p>
              <h1 className="mt-1 text-2xl font-semibold text-white">
                {agentMessage.heading}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-white/45">
                {agentMessage.body}
              </p>
            </div>
          </div>

          {selectedTemplate && currentStep !== "done" && (
            <div className="mb-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.14em] text-white/30">
                    Selected template
                  </p>
                  <h2 className="mt-2 text-lg font-semibold text-white">
                    {selectedTemplate.name}
                  </h2>
                  <p className="mt-1 text-sm text-white/45">
                    {selectedTemplate.description?.trim() ||
                      "No template description added yet."}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                      selectedClientCfg.color
                    )}
                  >
                    <Tag className="h-3.5 w-3.5" />
                    {selectedClientCfg.label}
                  </span>

                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                      selectedBuilderCfg.color
                    )}
                  >
                    {selectedBuilderCfg.icon}
                    {selectedBuilderCfg.label}
                  </span>

                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                      selectedFixedCfg.color
                    )}
                  >
                    {selectedFixedCfg.icon}
                    {selectedFixedCfg.label}
                  </span>
                </div>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <WizardInfoCard
                  label="Builder direction"
                  value={selectedBuilderCfg.description}
                />
                <WizardInfoCard
                  label="Fixed page direction"
                  value={selectedFixedCfg.description}
                />
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <WizardStatCard
                  label="Inherited services"
                  value={`${selectedServiceCount}`}
                  icon={<Wrench className="h-4 w-4" />}
                />
                <WizardStatCard
                  label="Inherited pages"
                  value={`${selectedPageCount}`}
                  icon={<FileStack className="h-4 w-4" />}
                />
                <WizardStatCard
                  label="Template tax rate"
                  value={selectedTaxRate}
                  icon={<FileText className="h-4 w-4" />}
                />
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {isGenerating && (
            <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-500/15 bg-amber-500/5 px-5 py-4">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-amber-400" />
              <div>
                <p className="text-sm font-medium text-amber-300">
                  Writing your proposal...
                </p>
                <p className="mt-0.5 text-xs text-white/30">
                  Generating scope descriptions and project summary for this
                  proposal.
                </p>
              </div>
            </div>
          )}

          <div className="ml-11">
            {currentStep === "template" && (
              <WizardStep1TemplateSelect
                selectedTemplateId={answers.templateId ?? initialTemplate?.id ?? undefined}
                onSelect={selectTemplate}
              />
            )}

            {currentStep === "client-info" && (
              <WizardStep2ClientInfo
                answers={answers}
                isCreating={isCreating}
                onSubmit={(values) =>
                  submitClientInfo({
                    prospectName: values.prospectName,
                    prospectEmail: values.prospectEmail ?? "",
                    prospectAddress: values.prospectAddress ?? "",
                    prospectCity: values.prospectCity ?? "",
                    prospectState: values.prospectState ?? "",
                    prospectZip: values.prospectZip ?? "",
                  })
                }
                onBack={goBack}
              />
            )}

            {currentStep === "project" && (
              <WizardStep3ProjectContext
                answers={answers}
                isGenerating={isGenerating}
                onSubmit={submitProjectContext}
                onBack={goBack}
              />
            )}

            {currentStep === "review" && (
              <div className="space-y-5">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <p className="text-xs uppercase tracking-[0.14em] text-white/30">
                    Template → Proposal handoff
                  </p>
                  <p className="mt-2 text-sm leading-6 text-white/50">
                    This proposal is inheriting the selected template’s service mix,
                    page direction, builder mode, and AI context. Review everything
                    below before finalizing and moving into PDF, send, and sign flow.
                  </p>
                </div>

                <WizardStep4Review
                  answers={answers}
                  isCreating={isCreating}
                  isGenerating={isGenerating}
                  onBack={goBack}
                  onUpdateLineItem={updateLineItem}
                  onUpdateAnswers={updateAnswers}
                  onConfirm={finalizeProposal}
                  confirmLabel={isEditMode ? "Save changes" : "Create proposal"}
                />
              </div>
            )}

            {currentStep === "done" && (
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-8">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
                    <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold text-white">
                      {isEditMode
                        ? "Proposal updated"
                        : "Proposal draft created"}
                    </h2>
                    <p className="mt-2 max-w-xl text-sm leading-6 text-white/45">
                      {isEditMode
                        ? "Your changes have been saved. Opening proposal details now."
                        : "Your proposal draft has been created. Opening proposal details now."}
                    </p>

                    <div className="mt-5 inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-white/70">
                      Opening proposal details
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <aside className="hidden w-80 shrink-0 xl:block">
          <div className="sticky top-24 space-y-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <p className="text-xs font-medium uppercase tracking-[0.14em] text-white/35">
                Workflow
              </p>

              <div className="mt-4 space-y-3">
                {STEPS.map((step, idx) => {
                  const isCompleted = idx < currentStepIndex;
                  const isCurrent = step.id === currentStep;

                  return (
                    <div
                      key={step.id}
                      className={cn(
                        "rounded-xl border px-4 py-3 transition-colors",
                        isCompleted &&
                          "border-emerald-500/20 bg-emerald-500/5 text-emerald-300",
                        isCurrent &&
                          "border-amber-500/20 bg-amber-500/5 text-amber-300",
                        !isCompleted &&
                          !isCurrent &&
                          "border-white/10 bg-black/20 text-white/40"
                      )}
                    >
                      <p className="text-sm font-medium">{step.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </aside>
      </div>
    </div>
  );
}

function WizardInfoCard({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
      <p className="text-[11px] uppercase tracking-[0.14em] text-white/30">
        {label}
      </p>
      <p className="mt-2 text-sm leading-6 text-white/65">{value}</p>
    </div>
  );
}

function WizardStatCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
      <div className="flex items-center gap-2 text-white/35">
        {icon}
        <span className="text-[11px] uppercase tracking-[0.14em]">{label}</span>
      </div>
      <p className="mt-3 text-lg font-semibold text-white/85">{value}</p>
    </div>
  );
}
